<template>
	<div class="today-summary">
		<!-- header -->
		<div class="d-flex align-center px-4 py-3 today-summary__header" @click="$router.back()">
			<v-icon class="me-2">{{ backIcon }}</v-icon>
			<span class="text-subtitle-1 font-weight-bold">{{ $T('title') }}</span>
		</div>

		<!-- sales / returns -->
		<div class="d-flex justify-start px-4 pt-3">
			<v-chip-group v-model="mode" mandatory>
				<v-chip
					value="sales"
					active-class="primary white--text"
					class="px-6 font-weight-medium today-summary__chip"
				>
					{{ $T('sales') }}
				</v-chip>

				<v-chip
					value="returns"
					active-class="primary white--text"
					class="px-6 font-weight-medium today-summary__chip"
				>
					{{ $T('returns') }}
				</v-chip>
			</v-chip-group>
		</div>

		<!-- selected date -->
		<div class="d-flex align-center px-4 pt-2">
			<v-icon small class="me-2">mdi-calendar</v-icon>
			<span class="text-body-2 font-weight-medium">{{ selectedDayLabel }}</span>
		</div>

		<!-- days picker -->
		<v-day-date
			v-model="selectedDay"
			:month="selectedMonth"
			:optional="false"
			class="my-2"
		/>

		<div class="px-3 today-summary__content">
			<loader v-if="loading"/>

			<!-- materials -->
			<v-expansion-panels v-else-if="materialGroups.length" flat class="today-summary__materials">
				<v-expansion-panel
					v-for="material in materialGroups"
					:key="material.id"
					class="today-summary__panel mb-3"
				>
					<v-expansion-panel-header hide-actions class="px-4 py-3">
						<template v-slot:default="{ open }">
							<div class="d-flex align-start">
								<span class="text-subtitle-2 font-weight-bold primary--text today-summary__title">
									{{ material.name }}
								</span>
								<span class="mx-2 today-summary__dashes"></span>
								<span class="text-subtitle-2 font-weight-bold primary--text flex-shrink-0 today-summary__amount">
									{{ $options.filters.formatNumber(material.total) }} {{ $T('currency') }}
								</span>
								<v-icon small class="ms-2 flex-shrink-0 today-summary__caret">
									{{ open ? 'mdi-chevron-down' : collapsedIcon }}
								</v-icon>
							</div>
						</template>
					</v-expansion-panel-header>

					<v-expansion-panel-content class="today-summary__panel-content">
						<v-simple-table dense class="today-summary__table">
							<thead>
								<tr class="primary">
									<th class="white--text text-start">{{ $T('customer') }}</th>
									<th class="white--text text-center">{{ $T('quantity') }}</th>
									<th class="white--text text-center">{{ $T('package-price') }} ({{ $T('currency') }})</th>
									<th class="white--text text-center">{{ $T('total-price') }}</th>
								</tr>
							</thead>

							<tbody>
								<template v-for="customer in material.customers">
									<!-- customer row -->
									<!-- @click="toggleCustomer(customer.key)" -->
									<tr
										:key="customer.key"
										:class="{ 'today-summary__row--open': isExpanded(customer.key) }"
										class="today-summary__row"
									>
										<td>
											<div class="d-flex align-center">
												<span>{{ customer.name }}</span>
												<v-icon v-if="false" small class="ms-1">
													{{ isExpanded(customer.key) ? 'mdi-chevron-down' : collapsedIcon }}
												</v-icon>
											</div>
										</td>
										<td class="text-center">{{ $options.filters.formatNumber(customer.quantity) }}</td>
										<td class="text-center">{{ $options.filters.formatNumber(customer.packagePrice) }}</td>
										<td class="text-center">{{ $options.filters.formatNumber(customer.total) }}</td>
									</tr>

									<!-- transactions of that customer -->
									<tr v-if="isExpanded(customer.key)" :key="`${customer.key}-transactions`" class="today-summary__inner-row">
										<td colspan="4" class="pa-0">
											<v-simple-table dense class="today-summary__inner-table">
												<thead>
													<tr>
														<th class="text-center grey--text text--darken-1 font-weight-regular">
															{{ $T('time') }}
														</th>
														<th class="text-center grey--text text--darken-1 font-weight-regular">
															{{ $T('total-amount') }}
														</th>
													</tr>
												</thead>

												<tbody>
													<tr v-for="transaction in customer.transactions" :key="transaction.id">
														<td class="text-center">{{ formatTime(transaction.createdAt) }}</td>
														<td class="text-center font-weight-bold">
															{{ $options.filters.formatNumber(transaction.total) }} {{ $T('currency') }}
														</td>
													</tr>
												</tbody>
											</v-simple-table>
										</td>
									</tr>
								</template>
							</tbody>
						</v-simple-table>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>

			<!-- empty -->
			<div
				v-else
				class="d-flex flex-column justify-center align-center grey--text text-body-1 py-10"
			>
				<v-icon x-large class="mb-3">mdi-database-off-outline</v-icon>
				{{ $T('no-data') }}
			</div>
		</div>

		<!-- grand total -->
		<div v-if="!loading && materialGroups.length" class="today-summary__total-bar px-3">
			<v-card outlined rounded="pill" class="d-flex align-center px-3 py-2">
				<v-avatar color="orange darken-2" size="32" class="me-2">
					<v-icon small color="white">mdi-cash-multiple</v-icon>
				</v-avatar>

				<span class="text-subtitle-2 font-weight-bold orange--text text--darken-2">
					{{ $T('grand-total') }} {{ $options.filters.formatNumber(grandTotal) }} {{ $T('currency') }}
				</span>
			</v-card>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import Loader from '@/components/Loader.vue'
import VDayDate from '@/components/VDayDate.vue'
import { accountsType } from '@/helpers/enums'

export default {
	name: 'TodaySummary',

	components: {
		Loader,
		VDayDate
	},

	data: () => ({
		loading: false,
		// نص وليس boolean: قيمة false في v-chip-group تلتبس مع "لا يوجد اختيار"
		mode: 'sales',
		selectedDay: moment().format('YYYY-MM-DD'),
		expandedCustomers: {},
	}),

	watch: {
		selectedDay() {
			this.reload()
		},

		mode() {
			this.reload()
		}
	},

	computed: {
		...mapState({
			statementTransactions: state => state.materialsTransactions.statementTransactions,
			materials: state => state.materials.materials,
			entities: state => state.entities.entities,
		}),

		...mapGetters({
			getMaterialById: 'materials/getMaterialById',
			getEntityByAccountId: 'entities/getEntityByAccountId',
		}),

		isReturns() {
			return this.mode === 'returns'
		},

		selectedMonth() {
			return moment(this.selectedDay).format('YYYY-MM')
		},

		selectedDayLabel() {
			const date = moment(this.selectedDay).format('DD MMMM YYYY')
			const isToday = this.selectedDay === moment().format('YYYY-MM-DD')
			return isToday ? `${this.$T('components.today')}, ${date}` : date
		},

		backIcon() {
			return this.$vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left'
		},

		collapsedIcon() {
			return this.$vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right'
		},

		/*
			الاستجابة مسطّحة (حركة واحدة لكل سطر)، فالمستويات الثلاثة في الواجهة تُبنى هنا:
			مادة -> زبون -> حركات، مع الحفاظ على ترتيب الظهور القادم من الـ API (SortByCreatedAt)
		*/
		materialGroups() {
			const groups = new Map()

			this.statementTransactions.forEach(transaction => {
				const materialId = this.materialId(transaction)
				const accountId = transaction.accountId
				const total = this.lineTotal(transaction)

				if (!groups.has(materialId)) {
					groups.set(materialId, {
						id: materialId,
						name: this.materialName(transaction, materialId),
						total: 0,
						customers: new Map()
					})
				}

				const material = groups.get(materialId)
				material.total += total

				if (!material.customers.has(accountId)) {
					material.customers.set(accountId, {
						key: `${materialId}-${accountId}`,
						name: this.customerName(transaction),
						quantity: 0,
						packagePrice: 0,
						total: 0,
						transactions: []
					})
				}

				const customer = material.customers.get(accountId)
				customer.quantity += this.activeAmount(transaction)
				customer.total += total

				// السعر ثابت لنفس المادة والزبون، لكن قد يغيب عن بعض الحركات
				if (!customer.packagePrice) customer.packagePrice = this.unitPrice(transaction)

				customer.transactions.push({
					id: transaction.id,
					createdAt: transaction.createdAt || transaction.date,
					total
				})
			})

			return Array.from(groups.values()).map(material => ({
				...material,
				customers: Array.from(material.customers.values())
			}))
		},

		// totalInAmount/totalOutAmount من الغلاف مجموع كميات لا مبالغ، فالإجمالي المالي يُحسب هنا
		grandTotal() {
			return this.statementTransactions.reduce((sum, transaction) => sum + this.lineTotal(transaction), 0)
		},
	},

	methods: {
		moment,

		/*
			الطرف الذي تحمله الحركة: المبيعات على الطرف الصادر والمرتجعات على الوارد.
			inWithName/outWithName يحملان اسم المادة لكن الـ API يعيدهما null في كثير من
			الحركات بينما in/out مملوءان، لذلك يُعتمد المسمّى أولاً ثم يُرجع إلى المختصر
		*/
		activeSide(transaction) {
			const outSide = transaction.outWithName || transaction.out
			const inSide = transaction.inWithName || transaction.in

			if (Number(outSide?.amount)) return outSide
			if (Number(inSide?.amount)) return inSide
			return outSide || inSide || null
		},

		activeAmount(transaction) {
			return Number(this.activeSide(transaction)?.amount) || 0
		},

		unitPrice(transaction) {
			const side = this.activeSide(transaction)
			return Number(side?.sellPrice ?? transaction.sellPrice) || 0
		},

		/*
			يعيد الـ API أحياناً totalSales = 0 لحركات لها كمية وسعر فعليان، فيُحتسب المبلغ
			عندها محلياً بنفس معادلة باقي النظام (priceDiscount نسبة مئوية لا مبلغاً)
		*/
		lineTotal(transaction) {
			const total = Number(transaction.totalSales)
			if (total) return total

			const side = this.activeSide(transaction)
			const discount = Number(side?.priceDiscount ?? transaction.priceDiscount) || 0
			const gross = this.activeAmount(transaction) * this.unitPrice(transaction)

			return gross - (discount / 100) * gross
		},

		materialId(transaction) {
			return this.activeSide(transaction)?.materialId
		},

		materialName(transaction, materialId) {
			return this.activeSide(transaction)?.materialName ||
				this.getMaterialById(materialId)?.name ||
				this.$T('unknown-material')
		},

		customerName(transaction) {
			return transaction.accountName ||
				this.getEntityByAccountId(transaction.accountId)?.name ||
				this.$T('unknown-customer')
		},

		formatTime(value) {
			return value ? moment(value).format('hh:mm A') : '-'
		},

		isExpanded(key) {
			return Boolean(this.expandedCustomers[key])
		},

		toggleCustomer(key) {
			this.$set(this.expandedCustomers, key, !this.expandedCustomers[key])
		},

		fetchStatement() {
			return this.$store.dispatch('materialsTransactions/fetchStatement', {
				fromDate: this.selectedDay,
				toDate: this.selectedDay,
				returnedTransactions: this.isReturns,
				type: accountsType.customers,
				sortByCreatedAt: true,
				getAll: true
			})
		},

		reload() {
			// المفاتيح مبنية على المادة والزبون، وقد لا يوجد أي منهما في اليوم التالي
			this.expandedCustomers = {}
			this.loading = true

			return this.fetchStatement().catch(() => {}).finally(() => {
				this.loading = false
			})
		},
	},

	created() {
		if (this.$route.query.date) this.selectedDay = this.$route.query.date

		this.loading = true
		Promise.all([
			this.fetchStatement().catch(() => {}),
			// أسماء المواد والزبائن تأتي مع الحركة عادةً، وهذه احتياط للحالات الناقصة
			!this.materials.length ? this.$store.dispatch('materials/fetchAll', { name: null, materialFormId: null }).catch(() => {}) : null,
			!this.entities.length ? this.$store.dispatch('entities/fetchAllData', { typeId: null, name: null, perPage: 1000 }).catch(() => {}) : null,
		]).finally(() => {
			this.loading = false
		})
	},

	beforeDestroy() {
		this.$store.dispatch('materialsTransactions/resetStatement')
	}
}
</script>

<style lang="scss">
// أرضية الصفحة وحدود العناصر تأتي من نظام التصميم في scss/variables.scss وscss/vuetify-overrides.scss
$border-color: var(--app-border-color);
$border: var(--app-border);

.today-summary {
	&__header {
		border-bottom: $border;
		background-color: #fff;
	}

	/*
		الشرائح بيضاء بحدّ رمادي بدل outlined، لأن Vuetify يفرض على
		‎.v-chip--outlined‎ خلفية شفافة بـ !important فتظهر بلون الأرضية.
		صنف primary في active-class يغلب على اللون والحدّ معاً
	*/
	&__chip {
		background-color: #fff !important;
		border: $border;
	}

	// مساحة أسفل المحتوى حتى لا يغطي شريط الإجمالي آخر بطاقة
	&__content {
		padding-bottom: 72px;
	}

	&__panel {
		border: $border;
		border-radius: 16px;

		&::before {
			box-shadow: none;
		}
	}

	// خط فاصل بين ترويسة البطاقة والجدول، كما في التصميم
	&__panel-content .v-expansion-panel-content__wrap {
		padding: 0 16px 16px;
		border-top: $border;
		padding-top: 14px;
	}

	// العنوان يلتف على سطرين والخط المتقطع يملأ ما تبقى من السطر الأول
	&__title {
		max-width: 55%;
		min-width: 0;
		line-height: 1.4;
	}

	&__dashes {
		flex: 1 1 auto;
		min-width: 16px;
		margin-top: 9px;
		border-bottom: 1px dashed $border-color;
	}

	&__amount,
	&__caret {
		margin-top: 1px;
	}

	/*
		قواعد v-data-table في Vuetify عالية التخصيص (‎.v-data-table > __wrapper > table > …‎)،
		لذلك تُكتب المحدِّدات على نفس الشكل ليفوز التنسيق دون !important أو ‎:deep‎
	*/
	&__table.v-data-table {
		border: $border;
		border-radius: 10px;
		overflow: hidden;

		> .v-data-table__wrapper > table {
			// الترويسة الخضراء بفواصل بين الأعمدة
			> thead > tr > th {
				height: 38px;
				font-size: 0.75rem;
				border-bottom: none;

				& + th {
					border-inline-start: $border;
				}
			}

			> tbody > tr > td {
				height: 40px;
				font-size: 0.8125rem;
				border-bottom: $border;

				& + td {
					border-inline-start: $border;
				}
			}

			// الجدول محاط بحدّه الخارجي، فلا حاجة لخط أسفل آخر سطر
			> tbody > tr:last-child > td {
				border-bottom: none;
			}
		}
	}

	&__row {
		cursor: pointer;
	}

	&__row--open {
		background-color: #eef3f1;
	}

	// الحركات تظهر داخل الجدول على أرضية بيضاء وبدون فواصل عمودية
	&__inner-table.v-data-table {
		background-color: #fff;

		> .v-data-table__wrapper > table {
			> thead > tr > th {
				height: 34px;
				font-size: 0.75rem;
				border-bottom: $border;
			}

			> tbody > tr > td {
				height: 34px;
				font-size: 0.8125rem;
				border-bottom: none;
			}

			> tbody > tr + tr > td {
				border-top: $border;
			}
		}
	}

	// يظهر الشريط عائقاً فوق المحتوى كما في التصميم
	&__total-bar {
		position: fixed;
		left: 0;
		right: 0;
		bottom: 12px;
	}
}

/*
	لا حاجة هنا لتنسيق شريط الأيام ولا لحدّ v-card:
	الأول انتقل إلى VDayDate.vue، والثاني يأتي من مفتاح dividers في scss/variables.scss
*/
</style>
